import React, { useState } from "react";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import Header from "../components/Header";
import Notification from "../components/Notification";
import { API_URL } from "../config";
import '../styles/SignIn.css';

function ProfileReminders() {
    const [email, setEmail] = useState("");
    const [action, setAction] = useState("unsubscribe"); // unsubscribe or resubscribe
    const [notification, setNotification] = useState({ type: "", message: "", isVisible: false });
    const [errors, setErrors] = useState({
        email: ""
    });
    const [loading, setLoading] = useState(false);
    const [currentStatus, setCurrentStatus] = useState(null);

    const validate = () => {
        let isValid = true;
        const errors = {};

        if (!email) {
            errors.email = "Email is required.";
            isValid = false;
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            errors.email = "Email is invalid.";
            isValid = false;
        }

        setErrors(errors);
        return isValid;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        
        if (validate()) {
            try {
                const endpoint = action === "unsubscribe" 
                    ? "/profile/reminders/unsubscribe"
                    : "/profile/reminders/resubscribe";

                const response = await fetch(API_URL + endpoint, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        email: email
                    }),
                });

                const data = await response.json();

                if (response.ok) {
                    const successMessage = action === "unsubscribe" 
                        ? "You have been unsubscribed from profile reminders."
                        : "You have been resubscribed to profile reminders.";
                    
                    setNotification({ type: "success", message: successMessage, isVisible: true });
                    setCurrentStatus(data.data?.receive_reminders);
                    setEmail(""); // Clear email after successful action
                } else {
                    setNotification({ type: "danger", message: data.message || "Request failed. Please try again.", isVisible: true });
                    if (data.email) {
                        setErrors((prevErrors) => ({
                            ...prevErrors,
                            email: data.email.join(", "),
                        }));
                    }
                }
            } catch (error) {
                console.log(error);
                setNotification({ type: "danger", message: "An error occurred. Please try again later.", isVisible: true });
            } finally {
                setLoading(false);
            }
        } else {
            setNotification({ type: "danger", message: "Please fix the errors in the form.", isVisible: true });
            setLoading(false);
        }
    };

    const checkStatus = async () => {
        if (!email) {
            setErrors({ email: "Email is required to check status." });
            return;
        }

        if (!validate()) return;

        setLoading(true);
        try {
            const response = await fetch(API_URL + "/profile/reminders/check-status", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    email: email
                }),
            });

            const data = await response.json();

            if (response.ok) {
                const statusText = data.data.receive_reminders 
                    ? "Currently receiving profile reminders" 
                    : "Not receiving profile reminders";
                const profileStatus = data.data.status ? ` (Profile status: ${data.data.status})` : '';
                
                setNotification({ 
                    type: "info", 
                    message: `${statusText}${profileStatus}`, 
                    isVisible: true 
                });
                setCurrentStatus(data.data.receive_reminders);
            } else {
                setNotification({ type: "danger", message: data.message || "Unable to check status.", isVisible: true });
            }
        } catch (error) {
            console.log(error);
            setNotification({ type: "danger", message: "An error occurred. Please try again later.", isVisible: true });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseNotification = () => {
        setNotification({ ...notification, isVisible: false });
    };

    return (
        <div>
            <Header />
            <main>
                <div className="signin-page mb-4">
                    <div className="siginin-bg"></div>
                    <div className="signin-card">
                        <div className="signin-content">
                            <div className="media pb-4 mb-4 is-justify-content-space-between">
                                <div className="media-left">
                                    <Link to="/"><i className="fa fa-arrow-left"></i> &nbsp; Back to Home</Link>
                                </div>
                                <div className="media-right">
                                    <Link to="/"><i className="fa-solid fa-x"></i></Link>
                                </div>
                            </div>
                            <div className="content">
                                <h1 className="sign-in-title">Profile Reminder Preferences</h1>

                                <p>Manage your profile reminder notifications. Enter your email address to unsubscribe from or resubscribe to profile renewal reminders.</p>

                                {notification.isVisible && (
                                    <Notification
                                        type={notification.type}
                                        message={notification.message}
                                        onClose={handleCloseNotification}
                                    />
                                )}

                                <form onSubmit={handleSubmit}>
                                    <div className={`field ${errors.email ? 'is-danger' : ''}`}>
                                        <label className="label">Email Address</label>
                                        <div className="control has-icons-left has-icons-right">
                                            <input
                                                className={`input ${errors.email ? 'is-danger' : ''}`}
                                                type="email"
                                                placeholder="Enter your email address"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                            />
                                            <span className="icon is-small is-left">
                                                <i className="fas fa-envelope"></i>
                                            </span>
                                            {errors.email && (
                                                <span className="icon is-small is-right">
                                                    <i className="fas fa-exclamation-triangle"></i>
                                                </span>
                                            )}
                                        </div>
                                        {errors.email && <p className="help is-danger">{errors.email}</p>}
                                    </div>

                                    <div className="field">
                                        <div className="control">
                                            <button 
                                                type="button" 
                                                className="button is-fullwidth is-info mb-2"
                                                onClick={checkStatus}
                                                disabled={loading || !email}
                                            >
                                                {loading ? "Checking..." : "Check Current Status"}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="field is-grouped" style={{ gap: '10px' }}>
                                        <div className="control is-expanded">
                                            <button 
                                                type="submit" 
                                                className="button is-fullwidth is-danger"
                                                onClick={() => setAction("unsubscribe")}
                                                disabled={loading || !email}
                                            >
                                                {loading && action === "unsubscribe" ? "Processing..." : "Unsubscribe"}
                                            </button>
                                        </div>
                                        <div className="control is-expanded">
                                            <button 
                                                type="submit" 
                                                className="button is-fullwidth is-success"
                                                onClick={() => setAction("resubscribe")}
                                                disabled={loading || !email}
                                            >
                                                {loading && action === "resubscribe" ? "Processing..." : "Resubscribe"}
                                            </button>
                                        </div>
                                    </div>
                                </form>

                                <div className="content mt-5">
                                    <div className="notification is-light">
                                        <h4 className="is-size-6 has-text-weight-bold">What does this do?</h4>
                                        <ul className="mt-2">
                                            <li><strong>Unsubscribe:</strong> Stop receiving profile renewal reminder emails</li>
                                            <li><strong>Resubscribe:</strong> Start receiving profile renewal reminder emails again</li>
                                            <li><strong>Check Status:</strong> See your current subscription preference</li>
                                        </ul>
                                        <p className="mt-3 is-size-7">
                                            Note: This only affects profile renewal reminders. You may still receive other important notifications from our system.
                                        </p>
                                    </div>
                                </div>

                                <div className="field mb-5 mt-4">
                                    <div className="control">
                                        <label>
                                            Need help? &nbsp;
                                            <Link to="/contact">Contact Support</Link>
                                        </label>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}

export default ProfileReminders;